import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button, Flex, notification } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { Value } from 'platejs';
import * as Yup from 'yup';

import CalendarIcon from 'assets/images/icons/calendar.svg?react';
import LightningIcon from 'assets/images/icons/lightning.svg?react';
import { type OfferRead } from 'entities/Brand';
import { EditPostPageState, IPostFormState, usePostSlice } from 'entities/Post';
import {
	AutoDeletionAfterEnum,
	usePlannerPostsCreateMutation,
	usePlannerPostsPartialUpdateMutation,
} from 'features/Planner';
import { EMainRoutes, EYmPageScreen } from 'shared/typings';
import { BottomPanel, ButtonStyleLink } from 'shared/ui';
import { sendYmEvent } from 'shared/utils';

import { PostEditorPart } from './PostEditorForm/PostEditorPart';
import styles from './PostForm.module.scss';
import { PublishingSettingsPart } from './PublishingSettingsPart/PublishingSettingsPart';
import { SuccessPopup } from './SuccessPopup/SuccessPopup';

interface IPostFormProps {
	offer: OfferRead;
	plannerPostId?: string;
	pageState: EditPostPageState;
	setPageState: Dispatch<SetStateAction<EditPostPageState>>;
}

export const PostForm: FC<IPostFormProps> = ({ offer, pageState, plannerPostId, setPageState }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const isEdit = !!plannerPostId;

	const [editingTime, setEditingTime] = useState<Date | null>(null);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
	const { formState, setFormState } = usePostSlice();
	const [plateValue, setPlateValue] = useState<Value | undefined>(undefined);

	const initialState: IPostFormState = {
		channel_ids: formState?.channel_ids || null,
		date: formState?.date || dayjs().set('second', 1),
		time: formState?.time || dayjs().add(1, 'minute').set('second', 1),
		auto_deletion_after: formState?.auto_deletion_after || AutoDeletionAfterEnum.Never,
		content: formState?.content || offer?.posts[0]?.content,
		publishNow: formState?.publishNow || false,
		images: formState?.images || null,
	};

	const [createPost, { isLoading, isSuccess, reset, data }] = usePlannerPostsCreateMutation();
	const [updatePost, { isLoading: isUpdateLoading, isSuccess: isUpdateSuccess, reset: resetUpdate, data: dataUpdate }] =
		usePlannerPostsPartialUpdateMutation();

	const { handleSubmit, setFieldValue, values, isValid } = useFormik({
		initialValues: initialState,
		validationSchema: Yup.object({
			channel_ids: Yup.string().required(t('Это поле обязательно для заполнения')),
			content: Yup.string().max(8192),
			publishNow: Yup.boolean(),
			date: Yup.string().when('publishNow', ([publishNow], schema) => {
				return publishNow ? schema.notRequired() : schema.required();
			}),
		}),
		onSubmit: (value) => {
			sendYmEvent(values.publishNow ? 'click_publish' : 'click_schedule', {
				page_screen: EYmPageScreen.CREATE_POST_SCREEN,
			});
			isEdit ? resetUpdate() : reset();
			const formData = new FormData();

			let publishedAt =
				!value.publishNow &&
				value.date
					?.set('hour', value.time?.hour() || 0)
					.set('minute', value.time?.minute() || 0)
					.set('second', 1)
					.toISOString();

			if (isEdit && value.publishNow) {
				publishedAt = dayjs().add(30, 'second').toISOString();
			}

			!isEdit && formData.append('post_id', offer.posts[0].uuid || '');
			isEdit && formData.append('planned_post_id', plannerPostId);

			formData.append('channel_ids', value.channel_ids || '');
			formData.append('auto_deletion_after', value.auto_deletion_after);
			value.content && formData.append('content', value.content);
			publishedAt && formData.append('published_at', publishedAt);
			value.images?.length &&
				value.images.forEach((image: UploadFile) => formData.append('images', image.originFileObj || ''));

			isEdit ? updatePost({ plannedPostId: plannerPostId, patchedUpdatePlannerPost: formData }) : createPost(formData);
		},
	});

	useEffect(() => {
		if (isSuccess || isUpdateSuccess) {
			setIsSuccessModalOpen(true);
		}
	}, [isSuccess, isUpdateSuccess]);

	useEffect(() => {
		const interval = setInterval(() => {
			if (isEdit && formState?.published_at && new Date(formState.published_at) < new Date()) {
				notification.info({
					message: t('Пост был опубликован. Сохранение невозможно. Вы можете запланировать публикацию повторно.'),
					placement: 'bottom',
					showProgress: true,
					pauseOnHover: true,
				});
				navigate(`/${EMainRoutes.POST}/${EMainRoutes.CREATE}?offerId=${offer.uuid}`);
				clearInterval(interval);
			}
		}, 1000);
		return () => clearInterval(interval);
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		return () => {
			setFormState(null);
		};
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			{pageState === EditPostPageState.PUBLISHING_SETTINGS && (
				<>
					<PublishingSettingsPart
						editingTime={editingTime}
						setPageState={setPageState}
						setFieldValue={setFieldValue}
						values={values}
					/>

					<BottomPanel>
						<Flex justify={'space-between'} gap={8} className={styles.postForm__footer}>
							{/*TODO: to */}
							<ButtonStyleLink size="large" to={`/${EMainRoutes.CATALOG}`}>
								{t('Назад')}
							</ButtonStyleLink>
							<Button
								disabled={!isValid || !values.channel_ids || !values.date || !values.time || !values.content}
								loading={isLoading || isUpdateLoading}
								size="large"
								type="primary"
								icon={values.publishNow ? <LightningIcon /> : <CalendarIcon />}
								onClick={() => handleSubmit()}>
								{values.publishNow ? t('Опубликовать') : isEdit ? t('Сохранить') : t('Запланировать')}
							</Button>
						</Flex>
					</BottomPanel>
				</>
			)}

			{pageState === EditPostPageState.POST_EDITOR && (
				<PostEditorPart
					values={values}
					setFieldValue={setFieldValue}
					offer={offer}
					setPageState={setPageState}
					setEditingTime={setEditingTime}
					plateValue={plateValue}
					setPlateValue={setPlateValue}
				/>
			)}

			{isSuccessModalOpen && (
				<SuccessPopup
					postType={values.publishNow ? 'instant' : 'regular'}
					postId={data?.id}
					open={isSuccessModalOpen}
					setIsSuccessModalOpen={setIsSuccessModalOpen}
					publishedAt={isEdit ? dataUpdate?.published_at : data?.published_at}
					isEdit={isEdit}
				/>
			)}
		</>
	);
};
